import React from 'react';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import PrivacyToggle from '@/components/biodata/PrivacyToggle';
import { Control, FieldPath, FieldValues } from 'react-hook-form';

interface FormFieldWithPrivacyProps<T extends FieldValues> {
  control: Control<T>;
  name: FieldPath<T>;
  label: string;
  type?: 'input' | 'textarea' | 'select';
  placeholder?: string;
  options?: { value: string; label: string }[];
  isPrivate: boolean;
  onPrivacyChange: (fieldName: string, isPrivate: boolean) => void;
  required?: boolean;
  description?: string;
}

function FormFieldWithPrivacy<T extends FieldValues>({
  control,
  name,
  label,
  type = 'input',
  placeholder,
  options = [],
  isPrivate,
  onPrivacyChange,
  required = false,
  description,
}: FormFieldWithPrivacyProps<T>) {
  const fieldName = name as string;

  const renderInput = (field: any) => {
    switch (type) {
      case 'textarea':
        return (
          <Textarea
            placeholder={placeholder}
            {...field}
            value={field.value || ''}
          />
        );
      case 'select':
        return (
          <Select onValueChange={field.onChange} value={field.value}>
            <SelectTrigger>
              <SelectValue placeholder={placeholder || `Select ${label}`} />
            </SelectTrigger>
            <SelectContent>
              {options.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      default:
        return (
          <Input
            type="text"
            placeholder={placeholder}
            {...field}
            value={field.value || ''}
          />
        );
    }
  };

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem className="space-y-2">
          <div className="flex items-center justify-between">
            <FormLabel className="text-sm font-medium">
              {label} {required && <span className="text-red-500">*</span>}
            </FormLabel>
            <PrivacyToggle
              isPublic={!isPrivate}
              onChange={(isPublic) => onPrivacyChange(fieldName, !isPublic)}
              className="scale-90"
            />
          </div>
          
          <FormControl>
            {renderInput(field)}
          </FormControl>
          
          {description && (
            <p className="text-xs text-muted-foreground">{description}</p>
          )}
          
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

export default FormFieldWithPrivacy;